// createHtmlHistory.js
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useWesley } from '@anthill-modules/wesley-components';

import { InitializeUndoRedoStack, Redo, SaveEditorContentState, Undo } from '~/constants/mutations';
import { GetEditorContent, GetRedoStack, GetUndoStack } from '~/constants/getters';

export default () => {
  const store = useStore();
  const route = useRoute();
  const { iframeWindow, sendMessage } = useWesley();

  const saveEditorContentState = (data) => store.dispatch(`documentHistoryStore/${SaveEditorContentState}`, data);
  const dispatchUndo = async () => await store.dispatch(`documentHistoryStore/${Undo}`);
  const dispatchRedo = async () => await store.dispatch(`documentHistoryStore/${Redo}`);
  const initializeUndoRedo = async (data) =>
    await store.dispatch(`documentHistoryStore/${InitializeUndoRedoStack}`, data);

  const currentHtml = computed(() => store.getters[`documentHistoryStore/${GetEditorContent}`]);
  const undoStack = computed(() => store.getters[`documentHistoryStore/${GetUndoStack}`]);
  const redoStack = computed(() => store.getters[`documentHistoryStore/${GetRedoStack}`]);
  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);
  const sessionKey = computed(() => `undo-redo-${route.params.conversationId}`);

  const saveSessionState = () => {
    try {
      const payload = {
        undo: undoStack.value,
        redo: redoStack.value,
      };

      if (undoStack.value.length || redoStack.value.length) {
        sessionStorage.setItem(sessionKey.value, JSON.stringify(payload));
      }
    } catch (err) {
      if (err.name === 'QuotaExceededError') {
        console.warn('Session storage quota exceeded', err);
      } else {
        throw err;
      }
    }
  };

  // Load undo/redo from sessionStorage
  const loadStateFromSession = async (currentDocumentId) => {
    try {
      const saved = sessionStorage.getItem(`undo-redo-${currentDocumentId}`);
      if (saved) {
        const { undo = [], redo = [] } = JSON.parse(saved);
        await initializeUndoRedo({ undo, redo });
      } else {
        await resetUndoRedoStack();
      }
    } catch (e) {
      console.warn('Failed to parse session history:', e);
    }
  };

  const setInitialTemplateOnStore = async (newHtml) => {
    await saveEditorContentState(newHtml);
  };

  const getDocumentContent = async () => {
    try {
      const iframe = iframeWindow.value;
      let content = '';
      if (iframe && iframe.document) {
        // Detroying any potential inline text editor instance, to avoid saving a contenteditable state
        sendMessage({ method: 'editorDestroy' });
        // Cloning to not affect the actual displayed content
        const docElem = iframe.document.documentElement.cloneNode(true);
        // Removing any Wesley specific elements from the DOM before saving
        const selector = `
          base,
          script[src*="/localhost:3434/"],
          script[src*="/wesley/"],
          script[src*="/web-editor/"],
          link[href*="/localhost:3434/"],
          link[href*="/wesley/"],
          link[href*="/web-editor/"],
          style#weStyles,
          style[data-tiptap-style]
        `;
        const toRemove = docElem.querySelectorAll(selector);
        [...toRemove].forEach((elem) => elem.remove());
        // Get the content after removing Wesley elements
        content = docElem.outerHTML;
      }
      return content;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const saveEditorContentStateFromIframe = (withSessionStorage = false) => {
    if (withSessionStorage) {
      saveSessionState();
    }
    setTimeout(async () => {
      const content = await getDocumentContent();
      if (content) saveEditorContentState(content);
    }, 100);
  };

  const undo = async () => {
    await dispatchUndo();
  };

  const redo = async () => {
    await dispatchRedo();
  };

  const resetUndoRedoStack = async () => {
    await initializeUndoRedo({ undo: [], redo: [] });
  };

  return {
    currentHtml,
    setInitialTemplateOnStore,
    undo,
    redo,
    getDocumentContent,
    saveEditorContentStateFromIframe,
    canUndo,
    canRedo,
    loadStateFromSession,
    resetUndoRedoStack,
  };
};
